import { getReplay } from '@sentry/nextjs';
import { IncomingMessage } from 'http';
import { crush, isEqual } from 'radash';
import { mutate } from 'swr';
import { snapshot } from 'valtio';

import { publicConfig } from '@public-config';

import { User, UserAgreementType, UserInvitation } from 'models';
import { ProjectHelpers } from 'helpers/ProjectHelpers';
import { UserHelpers } from 'helpers/UserHelpers';

import { ApiService } from 'services/ApiService';
import { ImpersonationService } from 'services/ImpersonationService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { state as currentUserState } from 'state/current-user';

import BeaconService from './BeaconService';

export type UserInvitationProps = {
    email?: string;
    userId?: string;
    companyId?: string;
};

const UserService = {
    getCurrentUser: async (): Promise<User | null> => {
        const response = await ApiService.get(`${publicConfig.urls.api}/users/me`);

        return response?.user ?? null;
    },

    refreshCurrentUser: async (userToSet?: User) => {
        currentUserState.user = userToSet ?? (await UserService.getCurrentUser());
    },

    get: async (userId: string, req?: IncomingMessage): Promise<User> => {
        if (currentUserState.user?.id === userId) {
            return snapshot(currentUserState.user) as User;
        }

        return ApiService.get(`${publicConfig.urls.api}/users/${userId}?depth=0`, { req });
    },

    getMultiple: async (userIds: string[]) => {
        if (userIds.length === 0) {
            return {
                docs: [],
                users: [],
            };
        }

        const response = await ApiService.get<{
            docs: User[];
        }>(`${publicConfig.urls.api}/users/get-by-ids?${userIds.map((id) => `ids=${id}`).join('&')}`);

        return {
            ...response,
            users: response.docs,
        };
    },

    update: async (userId: string, payload: Partial<User>, track: boolean = true) => {
        if (track && currentUserState.user) {
            const payloadValues = crush(payload);
            const userValues = crush(currentUserState.user) as Record<string, unknown>;

            const ignoredKeys = ['isLoading', 'isManufacturer', 'hasCompany', 'isSupportUser'];
            const changes = Object.entries(payloadValues).filter(
                ([key, value]) => !ignoredKeys.includes(key) && !isEqual(value, userValues[key]),
            );

            if (changes.length) {
                InternalTrackingService.track('user.update', Object.fromEntries(changes));
            }
        }

        await ApiService.patch(`${publicConfig.urls.api}/users/${userId}`, payload);
        await UserService.refreshCurrentUser();
    },

    switchTeam: async (teamId: string) => {
        InternalTrackingService.track('team.switch', {
            team: teamId,
        });

        return ApiService.post(`${publicConfig.urls.api}/users/switch-team`, { teamId });
    },

    heartbeat: async () => {
        return ApiService.get<{
            authenticated: boolean;
            release?: string;
        }>(`${publicConfig.urls.api}/users/heartbeat`, {}, 3);
    },

    ping: () => {
        if (!InternalTrackingService.trackable() || !currentUserState.user?.name) {
            return;
        }

        navigator.sendBeacon(`${publicConfig.urls.api}/users/ping`);
    },

    pingProject: async (projectId: string) => {
        if (ImpersonationService.isImpersonating()) {
            return;
        }

        await ApiService.post(
            `${publicConfig.urls.api}/users/ping-project`,
            { projectId, replayId: getReplay()?.getReplayId() },
            { keepalive: true, hideConsoleError: true },
        );
        await mutate(ProjectHelpers.swr.recentProjects());
    },

    pingProduct: (componentId: string) => {
        if (ImpersonationService.isImpersonating()) {
            return;
        }

        BeaconService.send('/users/ping-product', { componentId, replayId: getReplay()?.getReplayId() });
    },

    pingProfile: (profileId: string) => {
        if (ImpersonationService.isImpersonating()) {
            return;
        }

        BeaconService.send('/users/ping-profile', { profileId, replayId: getReplay()?.getReplayId() });
    },

    sendNewUserInvites: async (emails: string[], teamId: string) => {
        return ApiService.post(`${publicConfig.urls.api}/users/send-new-user-invites`, {
            emails,
            teamId,
            source: location.toString(),
        });
    },

    finishSignup: async () => {
        await ApiService.post(`${publicConfig.urls.api}/users/finish-signup`, {}, { throwError: true });
    },

    dismissFeatureSuggestion: async (user: User, feature: string) => {
        InternalTrackingService.track('user.dismissFeatureSuggestion', {
            feature: feature,
        });

        const dismissedFeatureSuggestions = JSON.parse(JSON.stringify(user.dismissedFeatureSuggestions || []));

        dismissedFeatureSuggestions.push({
            feature: feature,
            data: new Date().toISOString(),
        });

        await UserService.update(user.id, {
            dismissedFeatureSuggestions,
        });
    },

    dismissCompanySuggestions: async (userId: string) => {
        InternalTrackingService.track('user.dismissCompanySuggestions');

        await UserService.update(userId, {
            companySuggestionsDismissed: true,
        });
    },

    dismissTeamSuggestions: async (userId: string) => {
        InternalTrackingService.track('user.dismissTeamSuggestions');

        await UserService.update(userId, {
            teamSuggestionsDismissed: true,
        });
    },

    getUserInvitation: async ({ email, userId, companyId }: UserInvitationProps) => {
        const params = new URLSearchParams();

        if (email) {
            params.set('where[email][equals]', email);
        }

        if (userId) {
            params.set('where[user][equals]', userId);
        }

        if (companyId) {
            params.set('where[references.company][equals]', companyId);
        }

        return ApiService.get<{ docs: UserInvitation[] }>(
            `${publicConfig.urls.api}/userInvitations?${params.toString()}`,
        );
    },

    inviteUser: async ({ companyId, email, message }: { companyId: string; email: string; message: object }) => {
        const result = await ApiService.post(`${publicConfig.urls.api}/userInvitations`, {
            references: {
                company: companyId,
            },
            email,
            message,
        });

        await mutate(UserHelpers.swr.userInvitations({ email, companyId }));

        return result;
    },

    agreements: {
        accepted: (agreementType: UserAgreementType): boolean => {
            const { agreements = [] } = currentUserState.user || {};

            return agreements.some((a) => a.type === agreementType && a.date);
        },

        accept: async (agreementType: UserAgreementType) => {
            const user = currentUserState.user;

            if (!user) return;

            InternalTrackingService.track('user.acceptAgreement', {
                agreementType,
            });

            const agreements = JSON.parse(JSON.stringify(user.agreements || []));

            const existingIndex = agreements.findIndex(
                (a: { type: UserAgreementType; date: string }) => a.type === agreementType,
            );

            if (existingIndex >= 0) {
                agreements[existingIndex] = {
                    type: agreementType,
                    date: new Date().toISOString(),
                };
            } else {
                agreements.push({
                    type: agreementType,
                    date: new Date().toISOString(),
                });
            }

            await UserService.update(user.id, {
                agreements,
            });
        },
    },
};

export { UserService };
